
import { Component, Mixins } from "vue-property-decorator";
import { AxiosInstance } from "axios";

import { JsonConsoleLogger } from "@/services/logging/consoleLogger";
import { Roster } from "@/interfaces/meeting/roster";
import {
  sendSignatory,
  submitSignature,
  loadMeetingState,
  placeOrder
} from "@/services/api/meeting";
import {
  sendSignatureChanged,
  sendSignatoryChanged
} from "@/services/meeting/messaging";
import CartMixin from "@/mixins/CartMixin.vue";
import ErrorMixin from "@/mixins/ErrorMixin.vue";
import SnackbarMixin from "@/mixins/SnackbarMixin.vue";
import { SignatoryResponse } from "@/interfaces/meeting/details";
import UserDetailsMixin from "@/mixins/UserDetailsMixin.vue";

@Component
export default class SignatureMixin extends Mixins<
  CartMixin,
  ErrorMixin,
  SnackbarMixin,
  UserDetailsMixin
>(CartMixin, ErrorMixin, SnackbarMixin, UserDetailsMixin) {
  isDisplayRequestSignatureDialog = false;
  isSubmittingSignature = false;
  isSubmittingReserveOrder = false;
  isSubmittingOrder = false;
  isOrderClicked = false;
  showNavigating = false;
  isReserveClicked = false;
  isLoading: Record<string, boolean> = {};
  signatureString = "";
  signatoryId = "";
  roster: Roster = {};

  get selectedSignatory() {
    return this.signatoryId || "";
  }

  get signatoryText() {
    return this.roster[this.signatoryId]?.name || this.$t("No signatory selected yet");
  }

  get isUserTheSignatory() {
    return this.selectedSignatory === this.detailsUserId;
  }

  setIsLoading(key: string, loading: boolean) {
    this.isLoading[key] = loading;
  }

  getIsLoading(key: string) {
    return this.isLoading[key] ?? false;
  }

  async updateSignatory(signatoryId: string) {
    // Close the dialog
    try {
      await sendSignatory(this.apiClient, { signatory: signatoryId });
      this.signatoryId = signatoryId;
      this.isDisplayRequestSignatureDialog = false;
      this.showSnackbar(this.$t("Signatory selected").toString());
      // Send socket event
      sendSignatoryChanged(signatoryId);
    } catch (ex) {
      this.setErrorMessages(
        "Unexpected error",
        "An error occured while selecting signatory, please try again or contact an administrator if error persist."
      );
      this.isDisplayErrorDialog = true;
    }
  }

  async sendSignature(apiClient: AxiosInstance, base64String: string) {
    this.signatureString = base64String;
    this.isSubmittingSignature = true;
    try {
      await submitSignature(apiClient, base64String);
      this.isSubmittingSignature = false;
      this.showSnackbar("The signature was submitted successfully.");
      // Send socket event
      sendSignatureChanged();
    } catch (ex) {
      this.isSubmittingSignature = false;
      this.setErrorMessages(
        "Unexpected error",
        "An error occured while submitting your signature, please try again or contact an administrator if error persist."
      );
      this.isDisplayErrorDialog = true;
    }
  }

  async loadSignatory(logger: JsonConsoleLogger) {
    try {
      const { signatory } = await loadMeetingState<SignatoryResponse>(
        this.apiClient,
        "signatory"
      );
      logger.info("[SIGNATORY] Signatory loaded from state", signatory);
      this.signatoryId = signatory;
    } catch (ex) {
      this.setErrorMessages(
        "Unexpected error",
        "An error occured while loading signatory, please try again or contact an administrator if error persist."
      );
      this.isDisplayErrorDialog = true;
    }
  }

  async placeOrder(
    apiClient: AxiosInstance,
    confirmed: boolean,
    extraContext?: any
  ) {
    try {
      this.isSubmittingOrder = confirmed;
      this.isSubmittingReserveOrder = !confirmed;
      this.isOrderClicked = confirmed;
      this.isReserveClicked = !confirmed;
      await placeOrder(apiClient, confirmed, false, extraContext);
      this.showSnackbar("The order was submitted successfully.");
    } catch (ex) {
      this.setErrorMessages(
        "Unexpected error",
        "An error occurred while placing your order, please try again or contact an administrator if error persist."
      );
      this.isDisplayErrorDialog = true;
    } finally {
      // Set both isSubmitting to false
      this.isSubmittingOrder = false;
      this.isSubmittingReserveOrder = false;
    }
  }

  // START - Handlers
  async onSignatureChanged(apiClient: AxiosInstance) {
    try {
      const { signature } = await loadMeetingState(apiClient, ["signature"]);
      this.signatureString = signature;
      this.showSnackbar("Signature is updated.");
    } catch (ex) {
      this.setErrorMessages(
        "Unexpected error",
        "An error occured while getting signature, please try again or contact an administrator if error persist."
      );
      this.isDisplayErrorDialog = true;
    }
  }
  // END - Handlers
}
